// Meal plan for the Your Tour confirmation letter.
//
// Travellers are full board, but the arrival and departure days only earn the
// meals they are actually at the hotel for. Those days are worked out from the
// flight times entered on the tour, allowing for the transfers either side:
// an hour from landing to reaching the hotel, and three hours from leaving the
// hotel to the flight.

// Meal service times, matching the itinerary builder defaults.
const BREAKFAST = 8 * 60;
const LUNCH     = 13 * 60;

// Transfer allowances.
const LANDING_TO_HOTEL = 60;
const HOTEL_TO_FLIGHT  = 180;

// Departure-day lunch is packed and taken to the airport, so it is set by its
// own cutoff rather than by the hotel's lunch service.
const PACKED_LUNCH_FROM = 15 * 60;

/**
 * Parses a free-text time into minutes past midnight. The flight time fields
 * are plain text, so this accepts the shapes staff actually type — "12:45",
 * "12.45", "1245", "9am", "2.45pm" — and returns null for anything it cannot
 * read, which drops the letter back to the generic wording.
 */
export function parseTimeToMinutes(value) {
  if (typeof value !== 'string') return null;

  const text = value.trim().toLowerCase();
  if (!text) return null;

  const meridiem = (/\b(am|pm)\b|(am|pm)/.exec(text) || [])[0] || null;
  const separated = /^(\d{1,2})\s*[:.h]\s*(\d{2})/.exec(text);
  const digits = text.replace(/\D/g, '');

  let hours;
  let minutes;

  if (separated) {
    hours = Number(separated[1]);
    minutes = Number(separated[2]);
  } else if (digits.length === 4) {
    // "1245"
    hours = Number(digits.slice(0, 2));
    minutes = Number(digits.slice(2));
  } else if (digits.length === 3) {
    // "845"
    hours = Number(digits.slice(0, 1));
    minutes = Number(digits.slice(1));
  } else if (digits.length > 0 && digits.length <= 2) {
    // "9am", "17"
    hours = Number(digits);
    minutes = 0;
  } else {
    return null;
  }

  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;

  return clampTime(hours, minutes);
}

function clampTime(hours, minutes) {
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function joinList(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Works out which meals each part of the tour includes.
 *
 * @param {object} request - Firestore request document data
 * @returns {{known: boolean, arrival: string[], departure: string[], fullDays: number, bulletText: string}}
 */
export function buildMealPlan(request) {
  const nights = Number(request?.quoteDetails?.nights) || 0;
  const fullDays = Math.max(0, nights - 1);

  const flightDetails = request?.flightDetails || {};
  const landing = parseTimeToMinutes(flightDetails.arrivalTime);
  const homeward = parseTimeToMinutes(flightDetails.returnTime);

  // Without both flight times we cannot say which meals fall inside the stay,
  // so the letter states the policy and promises detail once flights are set.
  if (landing == null || homeward == null || nights <= 0) {
    return {
      known: false,
      arrival: [],
      departure: [],
      fullDays,
      bulletText:
        'Full board — breakfast, lunch and evening meal on every full day. ' +
        'Meals on the arrival and departure days depend on the flight times, ' +
        'and will be confirmed as soon as flights are booked.',
    };
  }

  // Arrival day: lunch only if they reach the hotel before it is served. The
  // evening meal is a booked group sitting, so it is held for late arrivals.
  const arrival = [];
  if (landing + LANDING_TO_HOTEL <= LUNCH) arrival.push('lunch');
  arrival.push('evening meal');

  // Departure day: breakfast only if they are still at the hotel when it is
  // served — an early flight means leaving before that and getting nothing.
  const departure = [];
  if (homeward - HOTEL_TO_FLIGHT >= BREAKFAST) {
    departure.push('breakfast');
    if (homeward >= PACKED_LUNCH_FROM) departure.push('a packed lunch');
  }

  const parts = [`${joinList(arrival)} on arrival day`];

  if (fullDays > 0) {
    parts.push(
      `breakfast, lunch and evening meal on ${
        fullDays === 1 ? 'the full day' : `each of the ${fullDays} full days`
      }`
    );
  }

  parts.push(
    departure.length > 0
      ? `${joinList(departure)} on departure day`
      : 'no meals on departure day, as the return flight leaves too early for breakfast'
  );

  return {
    known: true,
    arrival,
    departure,
    fullDays,
    // Semicolons between the days, because each day's own meal list already
    // uses commas — joining these with "and" runs the whole thing together.
    bulletText: `Full board — ${parts.join('; ')}`,
  };
}
